/**
 * Small routed screens for platform services:
 *   /billing/success?session_id=…   Stripe Checkout return: confirm the session, then home
 *   /billing/cancel                 Stripe Checkout cancelled
 *   /today                          widget / PWA-shortcut entry: opens today's drawing prompt
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { applyServerEntitlement } from '../../lib/billing';
import { useT } from '../../lib/i18n';
import { currentSnapshot } from '../../lib/widget';
import { useApp } from '../../state/store';
import { Scribble } from '../../components/characters';
import { Button, StarsLayer } from '../../components/ui';

export function BillingReturn({ outcome }: { outcome: 'success' | 'cancel' }) {
  const t = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState<'confirming' | 'done' | 'later' | 'cancel'>(outcome === 'cancel' ? 'cancel' : 'confirming');
  const ran = useRef(false);

  useEffect(() => {
    if (outcome !== 'success' || ran.current) return;
    ran.current = true;
    if (!sessionId) return setState('later');
    void api
      .stripeConfirm(sessionId)
      .then((r) => {
        applyServerEntitlement(r.entitlement, r.entitlement.plan && r.entitlement.plan !== 'free' ? r.entitlement.plan : undefined);
        useApp.getState().logEvent('Purchase Completed', { store: 'stripe', trial: r.entitlement.isTrial });
        setState(r.entitlement.tier !== 'free' ? 'done' : 'later');
      })
      .catch(() => setState('later'));
  }, [outcome, sessionId]);

  const text = { confirming: 'svc.billing.confirming', done: 'svc.billing.success', later: 'svc.billing.pendingWebhook', cancel: 'svc.billing.cancelled' } as const;
  return (
    <div className="full bg-night svc-block" data-testid={`billing-${outcome}`}>
      <StarsLayer count={10} />
      <div className="col center svc-block-body">
        <Scribble mood={state === 'done' ? 'proud' : state === 'cancel' ? 'curious' : 'happy'} size={110} />
        <h2>{t(text[state])}</h2>
        {state !== 'confirming' && <Button onClick={() => nav('/', { replace: true })}>{t('continue')}</Button>}
      </div>
    </div>
  );
}

export function Today() {
  const onboarded = useApp((s) => s.onboarding.done);
  if (!onboarded) return <Navigate to="/" replace />;
  const snap = currentSnapshot();
  return <Navigate to={snap.prompt?.deepLink ?? '/playground'} replace />;
}
